import { useEffect, useState, KeyboardEvent } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useUserProfile } from "../../hooks/user/useUserProfile";
import { useAgentHistory } from "../../hooks/user/useAgentHistory";
import { useUserProfileUpdate } from "../../hooks/user/useUserProfileUpdate";
import { useInfiniteScroll } from "../../hooks/common/useInfiniteScroll";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import AgentHistoryCard from "../../components/user/AgentHistoryCard";
import AgentHistoryModal from "../../components/user/AgentHistoryModal";
import SidebarMenu, { MenuItemType } from "../../components/common/SidebarMenu";
import { AgentHistoryItem } from "../../types/user";
import {
  Mail,
  Phone,
  User,
  Calendar,
  Edit3,
  History,
  RefreshCw,
  Check,
  X,
  Users,
  Settings,
  Shield,
  FileText,
  Trash,
} from "lucide-react";
import { DebouncedInput } from "../../components/DebouncedInput";
import WithdrawModal from "../../components/user/WithdrawModal";

const profileUpdateSchema = z.object({
  nickname: z.string().min(2, "닉네임은 2자 이상이어야 합니다"),
  contact_hp: z
    .string()
    .regex(/^010-\d{4}-\d{4}$/, "올바른 휴대폰 번호 형식이 아닙니다"),
  gender: z.enum(["m", "f"], { required_error: "성별을 선택해주세요" }),
  birth: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}$/,
      "올바른 생년월일 형식이 아닙니다 (YYYY-MM-DD)"
    ),
});

type ProfileUpdateFormData = z.infer<typeof profileUpdateSchema>;

type TabType = "profile" | "history" | "withdraw";

const formatBirthDate = (value: string) => {
  value = value.replace(/\D/g, "");

  if (value.length > 4) {
    value = value.slice(0, 4) + "-" + value.slice(4);
  }
  if (value.length > 7) {
    value = value.slice(0, 7) + "-" + value.slice(7);
  }

  return value.slice(0, 10);
};

const formatPhoneNumber = (value: string) => {
  value = value.replace(/\D/g, "");

  if (value.length > 3) {
    value = value.slice(0, 3) + "-" + value.slice(3);
  }
  if (value.length > 8) {
    value = value.slice(0, 8) + "-" + value.slice(8);
  }

  return value.slice(0, 13);
};

const MyPage = () => {
  const { profile, isLoading, error, fetchProfile } = useUserProfile();
  const {
    historyItems,
    isLoading: isHistoryLoading,
    isLoadingMore,
    error: historyError,
    hasMore,
    fetchHistory,
    loadMore,
    refresh,
  } = useAgentHistory();

  const {
    isLoading: isUpdating,
    error: updateError,
    updateProfile,
    clearError,
  } = useUserProfileUpdate();

  const [activeTab, setActiveTab] = useState<TabType>("profile");
  const [selectedHistoryItem, setSelectedHistoryItem] =
    useState<AgentHistoryItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  const menuItems: MenuItemType[] = [
    {
      id: "profile",
      label: "내 정보",
      icon: <Settings className="w-4 h-4" />
    },
    {
      id: "history", 
      label: "사용 히스토리",
      icon: <History className="w-4 h-4" />
    },
    {
      id: "privacy",
      label: "개인정보 보호처리방침",
      icon: <Shield className="w-4 h-4" />,
      href: "/policy/privacy"
    },
    {
      id: "terms",
      label: "서비스 이용약관",
      icon: <FileText className="w-4 h-4" />,
      href: "/policy/terms"
    },
    {
      id: "withdraw",
      label: "회원탈퇴",
      icon: <Trash className="w-4 h-4" />,
    },
  ];

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ProfileUpdateFormData>({
    resolver: zodResolver(profileUpdateSchema),
    defaultValues: {
      nickname: "",
      contact_hp: "",
      gender: "m",
      birth: "",
    },
  });

  useInfiniteScroll({
    hasMore: activeTab === "history" ? hasMore : false,
    isLoading: isLoadingMore,
    loadMore: activeTab === "history" ? loadMore : () => {},
    threshold: 200,
  });

  useEffect(() => {
    fetchProfile();
    fetchHistory();
  }, [fetchProfile, fetchHistory]);

  const handleHistoryItemClick = (item: AgentHistoryItem) => {
    console.log("History item clicked:", item);
    setSelectedHistoryItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedHistoryItem(null);
  };

  const handleEditClick = () => {
    if (profile) {
      setValue("nickname", profile.nickname);
      setValue("contact_hp", profile.contact_hp);
      setValue("gender", profile.gender);
      setValue("birth", profile.birth);
      setIsEditMode(true);
      clearError();
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    reset();
    clearError();
  };

  const handleSaveProfile = async (data: ProfileUpdateFormData) => {
    const success = await updateProfile(data);
    if (success) {
      setIsEditMode(false);
      fetchProfile();
    }
  };

  const handleBirthDateChange = (value: string) => {
    const formatted = formatBirthDate(value);
    setValue("birth", formatted);
  };

  const handlePhoneNumberChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setValue("contact_hp", formatted);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleWithdrawModalClose = () => {
    setIsWithdrawModalOpen(false);
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 font-serif-kr">
        <div className="max-w-6xl mx-auto px-5 py-8 lg:py-12">
          <div className="mb-4 px-2 text-center lg:text-left">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-teal-600/60 via-green-500 to-emerald-600 bg-clip-text text-transparent mb-2">
              마이페이지
            </h1>
            <p className="text-gray-600 text-sm md:text-lg">계정 정보를 확인하고 관리하세요</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
            <SidebarMenu
              title="마이페이지"
              menuItems={menuItems}
              activeItem={activeTab}
              onItemClick={(itemId) => {
                if (itemId === "profile" || itemId === "history") {
                  setActiveTab(itemId as TabType);
                } else if (itemId === "withdraw") {
                  setIsWithdrawModalOpen(true);
                }
              }}
            />

            {/* 메인 콘텐츠 */}
            <div className="flex-1 min-w-0 lg:max-w-4xl">
              {isLoading ? (
                <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-8">
                  <div className="flex flex-col items-center justify-center py-12 lg:py-20">
                    <div className="relative">
                      <div className="animate-spin rounded-full h-12 w-12 lg:h-16 lg:w-16 border-4 border-gray-200"></div>
                      <div className="animate-spin rounded-full h-12 w-12 lg:h-16 lg:w-16 border-4 border-teal-500 border-t-transparent absolute top-0"></div>
                    </div>
                    <p className="text-gray-600 mt-4 text-base lg:text-lg">
                      프로필 정보를 불러오는 중...
                    </p>
                  </div>
                </div>
              ) : error ? (
                <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-8">
                  <div className="flex flex-col items-center justify-center py-12 lg:py-20">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 lg:p-6 text-center">
                      <p className="text-red-600 text-base lg:text-lg font-medium">{error}</p>
                      <button
                        onClick={fetchProfile}
                        className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        다시 시도
                      </button>
                    </div>
                  </div>
                </div>
              ) : profile ? (
                <>
                  {/* 프로필 관리 탭 */}
                  {activeTab === "profile" && (
                    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
                      <div className="relative bg-gradient-to-r from-teal-500 via-green-500/70 to-emerald-500/80 p-6 lg:p-8 text-white">
                        <div className="absolute top-4 right-4 lg:top-6 lg:right-6">
                          {!isEditMode ? (
                            <button
                              onClick={handleEditClick}
                              className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                            >
                              <Edit3 className="w-5 h-5" />
                            </button>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={handleCancelEdit}
                                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                              >
                                <X className="w-5 h-5" />
                              </button>
                              <button
                                onClick={handleSubmit(handleSaveProfile)}
                                disabled={isUpdating}
                                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors disabled:opacity-50"
                              >
                                <Check className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="relative mb-4 lg:mb-6">
                            <div className="w-20 h-20 lg:w-24 lg:h-24 bg-white/20 rounded-full flex items-center justify-center">
                              <User className="w-10 h-10 lg:w-12 lg:h-12" />
                            </div>
                          </div>
                          <h2 className="text-xl lg:text-2xl font-bold mb-1">
                            {profile.nickname}
                          </h2>
                          <p className="text-white/80 text-sm lg:text-base">Aivy 멤버</p>
                        </div>
                      </div>

                      <div className="px-4 py-4 lg:px-8 lg:py-6 space-y-4">
                        <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-xl">
                          <User className="w-6 h-6 text-teal-500" />
                          <div className="flex-1">
                            <p className="text-sm md:text-base text-gray-500 mb-1">닉네임</p>
                            {!isEditMode ? (
                              <p className="font-semibold text-gray-900">
                                {profile.nickname}
                              </p>
                            ) : (
                              <div>
                                <Controller
                                  name="nickname"
                                  control={control}
                                  render={({ field }) => (
                                    <DebouncedInput
                                      {...field}
                                      type="text"
                                      placeholder="닉네임"
                                      maxLength={10}
                                      delay={300}
                                      onChange={(value) => {
                                        field.onChange(value);
                                      }}
                                      onKeyDown={handleKeyDown}
                                    />
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-xl">
                          <Phone className="w-6 h-6 text-teal-500" />
                          <div className="flex-1">
                            <p className="text-sm md:text-base text-gray-500 mb-1">연락처</p>
                            {!isEditMode ? (
                              <p className="font-semibold text-gray-900">
                                {profile.contact_hp}
                              </p>
                            ) : (
                              <Controller
                                name="contact_hp"
                                control={control}
                                render={({ field }) => (
                                  <DebouncedInput
                                    {...field}
                                    type="text"
                                    placeholder="010-0000-0000"
                                    maxLength={13}
                                    delay={300}
                                    onChange={(value) => {
                                      field.onChange(value);
                                      handlePhoneNumberChange(value);
                                    }}
                                    onKeyDown={handleKeyDown}
                                  />
                                )}
                              />
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-xl">
                          <Mail className="w-6 h-6 text-teal-500" />
                          <div>
                            <p className="text-sm md:text-base text-gray-500 mb-1">이메일</p>
                            <p className="font-semibold text-gray-900">
                              {profile.email || "미등록"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-xl">
                          <Users className="w-6 h-6 text-teal-500" />
                          <div className="flex-1">
                            <p className="text-sm md:text-base text-gray-500 mb-1">성별</p>
                            {!isEditMode ? (
                              <p className="font-semibold text-gray-900">
                                {profile.gender === "m" ? "남성" : "여성"}
                              </p>
                            ) : (
                              <div>
                                <div className="flex gap-4">
                                  <label className="flex items-center">
                                    <input
                                      {...register("gender")}
                                      type="radio"
                                      value="m"
                                      className="mr-2"
                                    />
                                    남성
                                  </label>
                                  <label className="flex items-center">
                                    <input
                                      {...register("gender")}
                                      type="radio"
                                      value="f"
                                      className="mr-2"
                                    />
                                    여성
                                  </label>
                                </div>
                                {errors.gender && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {errors.gender.message}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-xl">
                          <Calendar className="w-6 h-6 text-teal-500" />
                          <div className="flex-1">
                            <p className="text-sm md:text-base text-gray-500 mb-1">생년월일</p>
                            {!isEditMode ? (
                              <p className="font-semibold text-gray-900">
                                {profile.birth}
                              </p>
                            ) : (
                              <Controller
                                name="birth"
                                control={control}
                                render={({ field }) => (
                                  <DebouncedInput
                                    {...field}
                                    type="text"
                                    placeholder="YYYY-MM-DD"
                                    maxLength={10}
                                    delay={300}
                                    onChange={(value) => {
                                      field.onChange(value);
                                      handleBirthDateChange(value);
                                    }}
                                    onKeyDown={handleKeyDown}
                                  />
                                )}
                              />
                            )}
                          </div>
                        </div>

                        {updateError && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-red-600 text-sm">{updateError}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 사용 히스토리 탭 */}
                  {activeTab === "history" && (
                    <div className="bg-white rounded-2xl shadow-md border border-gray-100 h-[600px] lg:h-[800px] flex flex-col">
                      <div className="flex items-center justify-between p-4 lg:p-6 pb-3 lg:pb-4 flex-shrink-0 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                          <History className="w-5 h-5 lg:w-6 lg:h-6 text-teal-500" />
                          <h3 className="text-lg lg:text-xl font-semibold">사용 히스토리</h3>
                        </div>
                        <button
                          onClick={refresh}
                          className="p-2 text-gray-500 hover:text-teal-500 hover:bg-gray-100 rounded-lg transition-colors"
                          disabled={isHistoryLoading}
                        >
                          <RefreshCw
                            className={`w-5 h-5 ${
                              isHistoryLoading ? "animate-spin" : ""
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex-1 overflow-hidden px-4 lg:px-6 pb-4 lg:pb-6">
                        {isHistoryLoading ? (
                          <div className="flex flex-col items-center justify-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent mb-4"></div>
                            <p className="text-gray-600">
                              히스토리를 불러오는 중...
                            </p>
                          </div>
                        ) : historyError ? (
                          <div className="flex flex-col items-center justify-center h-full">
                            <p className="text-red-600 mb-4">{historyError}</p>
                            <button
                              onClick={fetchHistory}
                              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            >
                              다시 시도
                            </button>
                          </div>
                        ) : historyItems.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full px-4">
                            <History className="w-10 h-10 lg:w-12 lg:h-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-base lg:text-lg text-center">
                              아직 사용한 에이전트가 없습니다.
                            </p>
                            <p className="text-gray-400 text-sm mt-2 text-center">
                              AI Studio에서 다양한 에이전트를 체험해보세요!
                            </p>
                          </div>
                        ) : (
                          <div className="h-full overflow-y-auto scrollbar-hover-stable pt-4 lg:pt-6">
                            <div className="space-y-4 pr-2">
                              {historyItems.map((item) => (
                                <AgentHistoryCard
                                  key={item.id}
                                  item={item}
                                  onClick={() => handleHistoryItemClick(item)}
                                />
                              ))}

                              {isLoadingMore && (
                                <div className="flex justify-center py-4">
                                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal-500 border-t-transparent"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-8">
                  <div className="flex flex-col items-center justify-center py-12 lg:py-20">
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 lg:p-8 text-center">
                      <User className="w-10 h-10 lg:w-12 lg:h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-base lg:text-lg">프로필 정보가 없습니다.</p>
                      <button
                        onClick={fetchProfile}
                        className="mt-4 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                      >
                        다시 불러오기
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* 히스토리 상세 모달 */}
      <AgentHistoryModal
        item={selectedHistoryItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* 회원탈퇴 모달 */}
      <WithdrawModal
        isOpen={isWithdrawModalOpen}
        onClose={handleWithdrawModalClose}
        userNickname={profile?.nickname}
      />

      <Footer />
    </>
  );
};

export default MyPage;
